import { useState } from "react"
import { motion } from "framer-motion"
import { useNavigate, Link } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { MailIcon, AlertCircle, CheckCircle2 } from 'lucide-react'
import { useEnhancedToast } from "@/components/ui/enhanced-toast"
import { useTenant } from "@/context/TenantContext"
import { useTheme } from "@/context/ThemeContext"
import { forgotPassword } from "@/api"

export default function ForgotPassword() {
    const [email, setEmail] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()
    const { success, error } = useEnhancedToast()
    const { branding } = useTenant()
    const { theme } = useTheme()

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)

    async function onSubmit(event: React.FormEvent) {
        event.preventDefault()
        setIsLoading(true)

        try {
            const result = await forgotPassword({email})
            if (result.error) {
                error({
                    icon: AlertCircle,
                    title: "Password Reset Failed",
                    description: result.error,
                    duration: 4000,
                });
                return;
            }
            success({
                icon: CheckCircle2,
                title: "Check Your Email",
                description: result.data?.message || "Password reset instructions have been sent to your email.",
                duration: 5000,
            });
            navigate("/auth/login", { replace: true })
        } finally {
            setIsLoading(false)
        }
    }

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    }

    const renderLogo = () => {
        if (!effectiveLogoUrl) {
            return (
                <span className="text-2xl font-semibold text-primary">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img
                src={effectiveLogoUrl}
                alt={branding?.company_name || 'Company Logo'}
                className="max-h-12 sm:max-h-14 w-auto max-w-[180px] sm:max-w-[220px] object-contain"
                onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-2xl font-semibold text-primary">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-md mx-auto"
        >
            <Card className="shadow-lg border">
                <CardContent className="p-6 sm:p-8 space-y-6">
                    {/* Logo */}
                    <div className="flex flex-col items-center space-y-6">
                        <div className="flex items-center justify-center min-h-12 max-h-16 py-2">
                            {renderLogo()}
                        </div>
                        <div className="text-center space-y-1.5">
                            <h1 className="text-xl font-semibold tracking-tight">Forgot your password?</h1>
                            <p className="text-sm text-muted-foreground/80">
                                No worries! Enter your email and we'll send you reset instructions.
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={onSubmit} className="space-y-4">
                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-sm font-medium">
                                Email
                            </Label>
                            <div className="relative">
                                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    placeholder="Enter your email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="pl-9 h-9"
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            className="w-full h-9"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </span>
                            ) : (
                                'Reset Password'
                            )}
                        </Button>
                    </form>

                    {/* Login Link */}
                    <div className="text-center text-sm text-muted-foreground/80">
                        Remember your password?{" "}
                        <Link 
                            to="/auth/login" 
                            className="text-primary hover:text-primary/80 transition-colors font-medium"
                        >
                            Sign in
                        </Link>
                    </div>
                </CardContent>
            </Card>

            {/* Footer Links */}
            <div className="mt-6 flex items-center justify-center gap-3 text-xs text-muted-foreground/70">
                <Link 
                    to="/terms-of-service" 
                    className="hover:text-foreground/80 transition-colors"
                >
                    Terms of Service
                </Link>
                <span>•</span>
                <Link 
                    to="/privacy-policy" 
                    className="hover:text-foreground/80 transition-colors"
                >
                    Privacy Policy
                </Link>
            </div>
        </motion.div>
    )
}
